import { CommonModule, DatePipe } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import * as constants from 'src/app/common/constant/constants.type';
import { AppointmentStatusType } from 'src/app/common/enums/appointment-status-type';
import { toNativeDate } from 'src/app/common/function-helper';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { MaterialModule } from 'src/app/material.module';
import { TimeConvertPipe } from 'src/app/pipe/time-convert.pipe';
import { LookupService } from 'src/app/services/lookup.service';
import { AppointmentService } from '../appointment.service';
import { FollowUpAppointmentData } from '../iappointment';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { MY_FORMATS } from 'src/app/common/dateFormat';
import { FollowUpSlotPickerComponent } from '../follow-up-slot-picker/follow-up-slot-picker.component';

@Component({
  selector: 'app-follow-up-appointment',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    TablerIconsModule,
    FollowUpSlotPickerComponent,
  ],
  providers: [
    DatePipe,
    TimeConvertPipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  templateUrl: './follow-up-appointment.component.html',
  styleUrl: './follow-up-appointment.component.scss',
})
export class FollowUpAppointmentComponent implements OnInit, OnDestroy {
  @ViewChild(FollowUpSlotPickerComponent)followUpSlotPicker?: FollowUpSlotPickerComponent;

  private appointmentTypeList: CommonDropDown[] = [];
  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: FollowUpAppointmentData,
    private readonly dialogRef: MatDialogRef<FollowUpAppointmentComponent>,
    private readonly lookupService: LookupService,
    private readonly appointmentService: AppointmentService,
    private readonly datePipe: DatePipe,
    private readonly timeConvertPipe: TimeConvertPipe,
    private readonly toastr: ToastrService,
    private readonly translate: TranslateService,
    private readonly translationUtils: TranslationUtils,
  ) {}

  ngOnInit(): void {
    this.lookupService
      .getAppointmentTypeLookup()
      .pipe(takeUntil(this.destroy$))
      .subscribe((types) => {
        this.appointmentTypeList = types;
      });
  }

  onCloseDialog(): void {
    this.dialogRef.close();
  }

  onSaveFollowUp(): void {
    if (!this.followUpSlotPicker || this.followUpSlotPicker.invalid) {
      this.followUpSlotPicker?.markAllAsTouched();
      return;
    }

    if (!this.data.patientId || !this.data.doctorId) {
      this.toastr.error(
        this.translate.instant(
          'APPOINTMENT_RELATED.FOLLOW_UP_CONTEXT_REQUIRED',
        ),
      );
      return;
    }

    const followUpType = this.resolveFollowUpAppointmentType();
    if (!followUpType) {
      this.toastr.error(
        this.translate.instant('APPOINTMENT_RELATED.FOLLOW_UP_TYPE_REQUIRED'),
      );
      return;
    }

    const selectedDate = this.followUpSlotPicker.value.appointDate;
    const selectedTime = this.followUpSlotPicker.value.appointTime;
    if (!selectedTime) {
      return;
    }

    const dateObj = toNativeDate(selectedDate);
    const formattedDate = this.datePipe.transform(
      dateObj,
      constants.DATE_FORMAT,
    );
    const formattedTime = this.timeConvertPipe.transform(selectedTime);

    if (!formattedDate || !formattedTime) {
      return;
    }

    const appointmentData = {
      patientId: this.data.patientId,
      doctorId: this.data.doctorId,
      appointmentTypeId: followUpType.id,
      appointDate: formattedDate,
      appointTime: formattedTime,
      status: AppointmentStatusType.Scheduled,
    };

    this.appointmentService
      .createAppointment(appointmentData as any)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response.isSuccess) {
          this.toastr.success(
            this.translationUtils.getTranslatedMessage(
              'COMMON.CREATE',
              this.translate.instant(
                'APPOINTMENT_RELATED.FOLLOW_UP_APPOINTMENT',
              ),
            ),
          );
          this.dialogRef.close(response);
          return;
        }

        this.toastr.error(
          Object.values(response.errors || {})
            .flat()
            .join('\n'),
        );
      });
  }

  private resolveFollowUpAppointmentType(): CommonDropDown | undefined {
    return this.appointmentTypeList.find(
      (type) => this.normalizeAppointmentTypeName(type.name) === 'followup',
    );
  }

  private normalizeAppointmentTypeName(name: string): string {
    return (name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
